/**
 * Auto-fire-once wrapper around `triggerBrowserPrint` for `print_document`
 * stream chunks.
 *
 * The browser-print machinery itself is chat-free and lives in
 * `@/utils/browserPrint`; what stays here is the part that only makes sense
 * beside Chat's stream vocabulary — reading a `PrintDocumentChunk` and firing
 * at most once per `tool_id`.
 *
 * The backend `print_document` tool returns a PDF URL with `action: "print"`
 * and leaves the actual printing to the client, so we fire on the `completed`
 * chunk. The backend only exposes `print_document` when `is_mobile === false`.
 */

import {
  __resetBrowserPrintForTests,
  triggerBrowserPrint,
} from '@/utils/browserPrint';

import type { PrintDocumentChunk } from '@heidi/evidence/contracts';

/**
 * Auto-fire dedupe cache. Stream chunks for a given `tool_id` can arrive
 * several times within a short window (React strict-mode double invocation,
 * reconnects, reducers replaying) — we want to print exactly once per tool
 * call. The short TTL + opportunistic prune on write bounds memory so the
 * cache can't grow across conversations for the lifetime of the tab.
 */
const AUTO_PRINT_DEDUPE_WINDOW_MS = 60_000;
const firedToolIds = new Map<string, number>();

const pruneExpiredFiredIds = (now: number): void => {
  for (const [toolId, firedAt] of firedToolIds) {
    if (now - firedAt > AUTO_PRINT_DEDUPE_WINDOW_MS) {
      firedToolIds.delete(toolId);
    }
  }
};

/**
 * Called from `useChatStream` for every `print_document` chunk. On the
 * `completed` chunk with a download URL we open the native print dialog
 * once per tool_id (React strict mode, rerenders, and duplicate chunks
 * are all deduped).
 */
export const handlePrintDocumentChunk = (chunk: PrintDocumentChunk): void => {
  if (chunk.status !== 'completed') return;
  if (!chunk.download_url) return;

  const now = Date.now();
  const lastFiredAt = firedToolIds.get(chunk.id);
  if (lastFiredAt !== undefined && now - lastFiredAt < AUTO_PRINT_DEDUPE_WINDOW_MS) {
    return;
  }
  firedToolIds.set(chunk.id, now);
  pruneExpiredFiredIds(now);

  void triggerBrowserPrint(chunk.download_url, { toolId: chunk.id });
};

/** Test-only: reset dedupe cache, cleanup timer, blob URL, and request counter. */
export const __resetPrintDocumentDedupeForTests = (): void => {
  firedToolIds.clear();
  __resetBrowserPrintForTests();
};
